import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { SlidersHorizontal, X } from 'lucide-react'
import { useCallback, useMemo, useState } from 'react'
import {
  columnCatalogQuery,
  matchCountQuery,
  matchesQuery,
  playerDetailQuery,
} from '@/api/queries'
import { Button } from '@/components/ui/controls'
import {
  DataCaveat,
  EmptyState,
  ErrorState,
  Skeleton,
  Spinner,
} from '@/components/ui/feedback'
import { MatchDetailPanel } from '@/features/match-detail/MatchDetail'
import { ColumnPicker } from '@/features/match-search/ColumnPicker'
import { FilterPanel } from '@/features/match-search/FilterPanel'
import {
  activeChips,
  hasAnyFilter,
  type MatchSearch,
  matchSearchSchema,
  PLAYER_FACETS,
  toApiFilters,
} from '@/features/match-search/filters'
import { ResultsTable } from '@/features/match-search/ResultsTable'
import { cn, formatCount } from '@/lib/utils'

export const Route = createFileRoute('/_app/matches/')({
  validateSearch: matchSearchSchema,
  component: MatchesPage,
})

function MatchesPage() {
  const search = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const [panelOpen, setPanelOpen] = useState(false)

  // Filters are URL state. Patching navigates, which re-keys the query — there
  // is no second copy of this state to fall out of sync.
  const setFilters = useCallback(
    (patch: Partial<MatchSearch>) => {
      navigate({
        search: (prev) => {
          const next = { ...prev, ...patch, match: undefined }
          // Clearing the player must clear everything attributed to them,
          // otherwise the URL holds a combination the API rejects with a 422.
          if ('player_id' in patch && !patch.player_id) {
            next.opponent_id = undefined
            for (const k of PLAYER_FACETS) next[k] = undefined
          }
          return next
        },
        replace: true,
      })
    },
    [navigate],
  )

  const reset = useCallback(() => navigate({ search: {}, replace: true }), [navigate])

  const apiFilters = useMemo(() => toApiFilters(search), [search])
  const selectedCols = useMemo(
    () => (search.cols ? search.cols.split(',').filter(Boolean) : []),
    [search.cols],
  )
  const view = useMemo(
    () => ({
      columns: search.cols || undefined,
      sort: search.sort || undefined,
      order: search.order,
    }),
    [search.cols, search.sort, search.order],
  )
  const catalog = useQuery(columnCatalogQuery())
  const extraColumns = useMemo(
    () =>
      selectedCols
        .map((k) => catalog.data?.columns.find((c) => c.key === k))
        .filter((c): c is NonNullable<typeof c> => Boolean(c)),
    [selectedCols, catalog.data],
  )
  const sortState = {
    key: search.sort ?? 'played_on',
    order: search.order ?? ('desc' as const),
  }

  // Clicking a header toggles direction; clicking a new one starts descending,
  // which is what people want for "most aces" and "longest match".
  const onSort = useCallback(
    (key: string) => {
      navigate({
        search: (p) => {
          const same = (p.sort ?? 'played_on') === key
          const next = same && (p.order ?? 'desc') === 'desc' ? 'asc' : 'desc'
          return key === 'played_on' && next === 'desc'
            ? { ...p, sort: undefined, order: undefined }
            : { ...p, sort: key, order: next }
        },
        replace: true,
      })
    },
    [navigate],
  )
  // Resolve the selected player so the chip reads "Djokovic N." rather than
  // "Player #1790". Cached, and the picker has usually fetched it already.
  const selectedPlayer = useQuery({
    ...playerDetailQuery(search.player_id ?? 0),
    enabled: Boolean(search.player_id),
  })
  const chips = activeChips(search, selectedPlayer.data?.player.display_name)
  const filtered = hasAnyFilter(search)

  const list = useInfiniteQuery(matchesQuery(apiFilters, view))
  const count = useQuery(matchCountQuery(apiFilters))

  const rows = useMemo(() => list.data?.pages.flatMap((p) => p.items) ?? [], [list.data])
  const withStats = useMemo(() => rows.filter((r) => r.has_stats).length, [rows])

  return (
    <div className="flex min-h-0 flex-1 overflow-hidden">
      {/* Filter rail: fixed on desktop, a sheet on mobile. */}
      <aside
        className={cn(
          // h-full + min-h-0 so the panel's own overflow-y-auto engages;
          // without a bounded height the document scrolls and the table leaves view
          'flex h-full min-h-0 w-72 shrink-0 flex-col border-r bg-app',
          'max-lg:fixed max-lg:inset-y-0 max-lg:left-0 max-lg:z-40 max-lg:w-80',
          'max-lg:transition-transform max-lg:duration-200',
          panelOpen ? 'max-lg:translate-x-0' : 'max-lg:-translate-x-full',
        )}
      >
        <FilterPanel
          value={search}
          onChange={setFilters}
          onReset={reset}
          hasFilters={filtered}
        />
      </aside>
      {panelOpen && (
        <button
          type="button"
          aria-label="Close filters"
          onClick={() => setPanelOpen(false)}
          className="fixed inset-0 z-30 bg-black/50 lg:hidden"
        />
      )}

      <section className="flex min-w-0 flex-1 flex-col">
        <div className="flex flex-wrap items-center gap-2 border-b px-4 py-3">
          <Button
            variant="secondary"
            size="sm"
            className="lg:hidden"
            onClick={() => setPanelOpen(true)}
          >
            <SlidersHorizontal className="size-3.5" aria-hidden />
            Filters
          </Button>

          <h1 className="font-medium text-fg text-sm">
            {count.isPending ? (
              <Skeleton className="inline-block h-4 w-24 align-middle" />
            ) : count.data ? (
              <>
                {formatCount(count.data.count, count.data.capped)}{' '}
                <span className="text-muted">
                  {count.data.count === 1 ? 'match' : 'matches'}
                </span>
              </>
            ) : (
              <span className="text-muted">Matches</span>
            )}
          </h1>

          <div className="flex min-w-0 flex-wrap items-center gap-1.5">
            {chips.map((c) => (
              <button
                key={c.key}
                type="button"
                onClick={() => setFilters({ [c.key]: undefined })}
                className={cn(
                  'inline-flex items-center gap-1 rounded border px-2 py-0.5',
                  'text-xs text-muted capitalize hover:bg-[var(--bg-hover)] hover:text-fg',
                )}
              >
                {c.label}
                <X className="size-3" aria-hidden />
              </button>
            ))}
          </div>

          <ColumnPicker
            selected={selectedCols}
            onChange={(keys) =>
              navigate({
                search: (p) => ({ ...p, cols: keys.join(',') || undefined }),
                replace: true,
              })
            }
          />
        </div>

        <div className="min-h-0 flex-1 overflow-auto">
          {list.isPending ? (
            <div className="space-y-1 p-4">
              {Array.from({ length: 12 }, (_, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
                <Skeleton key={i} className="h-9 w-full" />
              ))}
            </div>
          ) : list.isError ? (
            <ErrorState error={list.error as Error} retry={() => list.refetch()} />
          ) : rows.length === 0 ? (
            <EmptyState
              title="No matches found"
              description={
                search.season === 2020 && search.surface === 'grass'
                  ? 'The 2020 grass season was cancelled — Wimbledon did not take place. Try another season.'
                  : 'No matches fit these filters. Try removing one.'
              }
              action={
                filtered && (
                  <Button size="sm" onClick={reset}>
                    Reset filters
                  </Button>
                )
              }
            />
          ) : (
            <>
              <ResultsTable
                rows={rows}
                extraColumns={extraColumns}
                sort={sortState}
                onSort={onSort}
                selectedId={search.match}
                onSelect={(id) =>
                  navigate({ search: (p) => ({ ...p, match: id }), replace: false })
                }
              />
              <div className="flex flex-col items-center gap-3 p-6">
                {list.hasNextPage ? (
                  <Button
                    onClick={() => list.fetchNextPage()}
                    disabled={list.isFetchingNextPage}
                  >
                    {list.isFetchingNextPage && <Spinner />}
                    Load more
                  </Button>
                ) : (
                  <p className="text-xs text-subtle">
                    End of results — {rows.length.toLocaleString('en-GB')} shown
                  </p>
                )}
              </div>
            </>
          )}
        </div>

        {/* Coverage is stated where the results are, not buried in a help page:
            a filter that silently drops most of the sample is how people reach
            confident wrong conclusions. */}
        {rows.length > 0 && (
          <div className="border-t px-4 py-2">
            <DataCaveat
              tone={withStats === 0 || count.data?.stats_limited ? 'warn' : 'info'}
            >
              {count.data?.stats_limited ? (
                <>
                  This filter uses hold or break percentages, which exist for only about a
                  third of matches — the count above is a fraction of the matches that{' '}
                  <em>have</em> those statistics, not of all matches played.
                </>
              ) : (
                <>
                  {withStats} of {rows.length} shown have detailed serve and return
                  statistics. Across the whole dataset only about a third of matches do.
                </>
              )}
              {!search.has_stats && !count.data?.stats_limited && (
                <>
                  {' '}
                  <button
                    type="button"
                    className="underline hover:no-underline"
                    onClick={() => setFilters({ has_stats: true })}
                  >
                    Show only matches with statistics
                  </button>
                </>
              )}
            </DataCaveat>
          </div>
        )}
      </section>

      {/* Driven by ?match=<id>, so a detail view is linkable and the back
          button closes it — the same reason filters live in the URL. */}
      {search.match && (
        <MatchDetailPanel
          matchId={search.match}
          onClose={() => navigate({ search: (p) => ({ ...p, match: undefined }) })}
        />
      )}
    </div>
  )
}
